"use client";

import React, { useCallback, useState } from "react";

import {
  ALL_PORTFOLIO_ACCOUNTS_ID,
  getPortfolioScrubDisplay,
} from "@lattice/shared-frontend/charts";

import { IntervalPills } from "@lattice/frontend/components/charts-kit/IntervalPills";
import { ChartShimmer } from "@lattice/frontend/components/kit/Shimmer";
import { trpcApi } from "@lattice/frontend/lib/trpc/client";

import { HoldingsSection } from "./HoldingsSection";
import { PortfolioChart } from "./PortfolioChart";
import { PortfolioSummaryHeader } from "./PortfolioSummaryHeader";
import { PortfolioValueHeader } from "./PortfolioValueHeader";
import {
  PORTFOLIO_HOME_INTERVALS,
  type T_PortfolioHomeCanvasViewProps,
  type T_PortfolioHomeChartPoint,
  type T_PortfolioHomeInterval,
} from "./types";
import { usePortfolioHomeData } from "./usePortfolioHomeData";
import "./portfolio-home.css";

/** Pure visual surface catalogued with deterministic fixture data. */
export function PortfolioHomeCanvasView({
  interval,
  marketOneLiner,
  onAddManualHoldings,
  onConnectBrokerage,
  onIntervalChange,
  onSelectAccount,
  state,
}: T_PortfolioHomeCanvasViewProps) {
  const [scrubPoint, setScrubPoint] =
    useState<T_PortfolioHomeChartPoint | null>(null);
  const clearScrubPoint = useCallback(() => setScrubPoint(null), []);

  if (state.status === "loading") {
    return (
      <section aria-label="Portfolio overview" className="portfolio-home">
        <p className="portfolio-home__status">Loading your portfolio…</p>
        <ChartShimmer height={260} width={720} />
      </section>
    );
  }

  if (state.status === "error") {
    return (
      <section aria-label="Portfolio overview" className="portfolio-home">
        <p className="portfolio-home__status portfolio-home__status--error">
          {state.message}
        </p>
      </section>
    );
  }

  if (state.status === "empty") {
    return (
      <section aria-label="Portfolio overview" className="portfolio-home">
        <h2 className="portfolio-home__empty-title">No portfolio linked</h2>
        <p className="portfolio-home__status">
          Link a brokerage or add manual holdings to see your portfolio canvas.
        </p>
      </section>
    );
  }

  const { data } = state;
  const scrub = data.hasPersistedHistory
    ? getPortfolioScrubDisplay({
        chartPoints: data.chartPoints,
        scrubPoint,
      })
    : null;

  return (
    <section aria-label="Portfolio overview" className="portfolio-home">
      <PortfolioSummaryHeader
        dayPercentChange={data.dayPercentChange}
        direction={data.dayDirection}
        marketOneLiner={marketOneLiner}
      />
      <PortfolioValueHeader
        accountOptions={data.accountOptions}
        isPositive={data.isPositive}
        onAddManualHoldings={onAddManualHoldings}
        onConnectBrokerage={onConnectBrokerage}
        onSelectAccount={onSelectAccount}
        periodDollarChange={data.periodDollarChange}
        periodLabel={data.periodLabel}
        periodPercentChange={data.periodPercentChange}
        scrub={scrub}
        selectedAccountId={data.selectedAccountId}
        totalValue={data.totalValue}
      />
      <PortfolioChart
        isPositive={data.isPositive}
        onScrubEnd={clearScrubPoint}
        onScrubPoint={data.hasPersistedHistory ? setScrubPoint : undefined}
        points={data.chartPoints}
      />
      <IntervalPills
        active={interval}
        intervals={PORTFOLIO_HOME_INTERVALS}
        onChange={onIntervalChange}
      />
      <HoldingsSection holdings={data.holdings} />
    </section>
  );
}

/**
 * @storybook-internal
 * Data-wired shell surface; PortfolioHomeCanvasView owns the catalogued UI.
 */
export function PortfolioHomeCanvas({
  onAddManualHoldings,
  onConnectBrokerage,
}: {
  onAddManualHoldings: () => void;
  onConnectBrokerage: () => void;
}) {
  const [interval, setInterval] = useState<T_PortfolioHomeInterval>("1D");
  const [selectedAccountId, setSelectedAccountId] = useState(
    ALL_PORTFOLIO_ACCOUNTS_ID,
  );
  const state = usePortfolioHomeData(interval, selectedAccountId);
  const marketOneLiner = trpcApi.stock.get_market_one_liner.useQuery(
    undefined,
    {
      refetchOnWindowFocus: false,
      staleTime: 30 * 60 * 1000,
    },
  );

  return (
    <PortfolioHomeCanvasView
      interval={interval}
      marketOneLiner={marketOneLiner.data?.data.text ?? null}
      onAddManualHoldings={onAddManualHoldings}
      onConnectBrokerage={onConnectBrokerage}
      onIntervalChange={setInterval}
      onSelectAccount={setSelectedAccountId}
      state={state}
    />
  );
}
